import { Webshop } from '@kotipizzagroup/kotipizza-shared-types';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { SteppedContainer } from '../../../container/SteppedContainer';
import { Snackbar, contextType as InfoBoxTypes } from '../../../snackbar';
import { LoadingContainer } from '../../../loading/LoadingContainer';
import { mediaQuery, typography } from '../../../../theme';
import { colors } from '../../../../theme/color';
import { OrderType } from '../../../../types';
import { Primary as PrimaryButton } from '../../../button';
import { AddressSelectionContext } from '../../AddressSelectionProvider';
import { getRestaurantLimit, listHasOpenForDeliveryRestaurant } from '../../helpers';
import { useAddress } from '../../useAddress';
import { PreselectedRestaurantOutOfReach, RestaurantsNotFound } from '../../useRestaurants';
import { OrderTime } from './OrderTime';
import PreOrderRestaurantRow, { PreorderTimeSelection } from './PreOrderRestaurantRow';
import RestaurantRow from './RestaurantRow';
import { DynamicDeliveryFeeInfo, FreeDeliveryBanner, PreorderBanner } from './banners';
import { isRestaurantOpenForDeliveryType } from './helpers';
import { getActiveCampaignType } from './hooks/useDeliveryCampaign';
import { generatePreOrderTimesForWeek } from './preOrderHelpers';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { LoadingIconContainer } from '../../../loading/LoadingContainer';
import { AnalyticsPromotionName, analyticsEvents } from '../../../../utils/analytics/analytics';
import { useActiveFreeDeliveryCampaigns } from '../../../../clients/campaigns-api';
import {
  DeliveringRestaurantDto,
  RestaurantDetailsDto,
} from '@kotipizzagroup/kotipizza-shops-api-client';
import { isDeliveringRestaurant } from '../../../../clients/shops-api';
import { DeliveryCampaignType } from '../../types';
import { StyledAccordion, StyledAccordionDetails } from '../../../accordion/Accordion';
import { AccordionSummary } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

export const weekdayAverageDeliveryPrice = 4.59;
export const weekendAverageDeliveryPrice = 5.99;

type RestaurantListProps = {
  restaurants: (RestaurantDetailsDto | DeliveringRestaurantDto)[];
  isLoading: boolean;
  error: Error | RestaurantsNotFound | null;
};

export const RestaurantList: React.FC<RestaurantListProps> = ({
  restaurants,
  isLoading = false,
  error,
}) => {
  const { isPreorder, restaurantId, orderType, searchQuery, preorderTime } =
    useContext(AddressSelectionContext);

  const baseRestaurantLimit = getRestaurantLimit(orderType) || 0;
  const [restaurantLimitExtra, setRestaurantLimitExtra] = useState(0);
  const prevOrderTypeForLimitRef = useRef(orderType);

  useEffect(() => {
    if (prevOrderTypeForLimitRef.current === orderType) {
      return;
    }
    prevOrderTypeForLimitRef.current = orderType;
    queueMicrotask(() => {
      setRestaurantLimitExtra(0);
    });
  }, [orderType]);

  const restaurantLimit = baseRestaurantLimit + restaurantLimitExtra;

  const [changeLoading, setChangeLoading] = useState<boolean>(false);

  const { data: freeDeliveryCampaigns = [] } = useActiveFreeDeliveryCampaigns();

  const prevSearchQueryForLoadingPulseRef = useRef<string | null>(null);

  const visibleRestaurants = useMemo(() => {
    const openRestaurants = restaurants.filter(
      (restaurant) =>
        isRestaurantOpenForDeliveryType(restaurant, orderType).restaurantStatus !==
        Webshop.AvailabilityStatus.Hidden
    );

    return openRestaurants;
  }, [orderType, restaurants]);

  useEffect(() => {
    // Only flash loading when the user actually changes the search query — not on mount.
    // A mount-time pulse hides the list inside LoadingContainer and breaks tests (and UX)
    // when the parent tree re-renders/remounts (e.g. nested QueryClientProvider).
    if (prevSearchQueryForLoadingPulseRef.current === null) {
      prevSearchQueryForLoadingPulseRef.current = searchQuery;
      return;
    }
    if (prevSearchQueryForLoadingPulseRef.current === searchQuery) {
      return;
    }
    prevSearchQueryForLoadingPulseRef.current = searchQuery;

    const showLoadingId = setTimeout(() => {
      setChangeLoading(true);
    }, 0);
    const hideLoadingId = setTimeout(() => {
      setChangeLoading(false);
    }, 10);

    return () => {
      clearTimeout(showLoadingId);
      clearTimeout(hideLoadingId);
    };
  }, [searchQuery]);

  const isPickupOrEatin = orderType === OrderType.PICKUP || orderType === OrderType.EATIN;

  const listHasDynamicPrice = useMemo(() => {
    return (
      visibleRestaurants &&
      visibleRestaurants.some(
        (restaurant) =>
          isDeliveringRestaurant(restaurant) && restaurant.dynamicDeliveryFee !== undefined
      )
    );
  }, [visibleRestaurants]);

  const freeDeliveryCampaign = useMemo(() => {
    if (isLoading || freeDeliveryCampaigns.length < 1) {
      return undefined;
    }

    const globalDeliveryCampaign = freeDeliveryCampaigns.find(
      (campaign) => campaign.assignedShops && campaign.assignedShops.length < 1
    );

    if (globalDeliveryCampaign) {
      return globalDeliveryCampaign;
    }

    const visibleShopIds = restaurants.map((r) => r.restaurantId);
    return freeDeliveryCampaigns.find((campaign) =>
      (campaign.assignedShops || []).some((shopId) => visibleShopIds.includes(shopId))
    );
  }, [restaurants, isLoading, freeDeliveryCampaigns]);

  const activeDeliveryCampaign = getActiveCampaignType(isPreorder, freeDeliveryCampaign);

  const isDeliveryOrder = orderType === OrderType.DELIVERY;

  const errorMessage = useSearchResultStatus(visibleRestaurants, orderType, error);

  const shouldShowRestaurantSection = useMemo(
    () => visibleRestaurants.length > 0 || isLoading,
    [isLoading, visibleRestaurants.length]
  );

  const shownRestaurants = useMemo(
    () => (restaurantLimit > 0 ? visibleRestaurants.slice(0, restaurantLimit) : visibleRestaurants),
    [restaurantLimit, visibleRestaurants]
  );

  useEffect(() => {
    const currentListedOpenRestaurants = shownRestaurants
      .filter((r) => isRestaurantOpenForDeliveryType(r, orderType))
      .slice(restaurantLimit > 4 ? restaurantLimit - 5 : 0);

    if (currentListedOpenRestaurants.length > 0) {
      analyticsEvents.viewPromotion(
        currentListedOpenRestaurants.map((r) => ({
          promotion_name: AnalyticsPromotionName.RestaurantSelection,
          creative_name: r.displayName,
          creative_slot: '',
          delivery_time:
            orderType === OrderType.DELIVERY ? r.currentDeliveryEstimate : r.currentPickupEstimate,
          delivery_price: isDeliveringRestaurant(r)
            ? r.dynamicDeliveryFee || r.deliveryFee || 0
            : 0,
          delivery_type: orderType,
        }))
      );
    }
  }, [orderType, shownRestaurants, restaurantLimit]);

  const preOrderTimes = useMemo(
    () => (isPreorder ? generatePreOrderTimesForWeek(shownRestaurants, orderType) : []),
    [isPreorder, orderType, shownRestaurants]
  );

  return shouldShowRestaurantSection || error ? (
    <StyledLoadingContainer loading={isLoading || changeLoading}>
      {errorMessage?.contentBlocked && (
        <CenteredContainer>
          <RestaurantsWarningBox
            closable={false}
            context={errorMessage.severity}
            message={errorMessage.message}
            icon={<ErrorOutlineIcon style={{ fontSize: 32 }} />}
          />
        </CenteredContainer>
      )}
      {!errorMessage?.contentBlocked && (
        <>
          <SteppedContainer secondary stepNumber={3} title="Aika" preBorder>
            {errorMessage && !errorMessage.contentBlocked && (
              <RestaurantsWarningBox
                closable={false}
                context={errorMessage.severity}
                message={errorMessage.message}
                icon={<ErrorOutlineIcon style={{ fontSize: 32 }} />}
              />
            )}
            <OrderTime />
            {(activeDeliveryCampaign === DeliveryCampaignType.FREE_DELIVERY_CAMPAIGN ||
              activeDeliveryCampaign === DeliveryCampaignType.LOCAL_FREE_DELIVERY_CAMPAIGN) &&
              isDeliveryOrder && <FreeDeliveryBanner freeDeliveryCampaign={freeDeliveryCampaign} />}
            {isPreorder && (
              <>
                {orderType === OrderType.DELIVERY && !activeDeliveryCampaign && <PreorderBanner />}
                <StyledPreorderTimeSelection preOrderTimes={preOrderTimes} />
              </>
            )}
          </SteppedContainer>
          <SteppedContainer secondary stepNumber={4} title="Valitse Ravintola" noBorder>
            <RestaurantListSection>
              <div>
                {isPreorder ? (
                  preorderTime &&
                  shownRestaurants.map((restaurant) => (
                    <PreOrderRestaurantRow
                      key={restaurant.restaurantId}
                      active={restaurantId === restaurant.restaurantId}
                      restaurant={restaurant as unknown as DeliveringRestaurantDto}
                    />
                  ))
                ) : (
                  <>
                    {shownRestaurants.map((restaurant) => (
                      <RestaurantRow
                        key={restaurant.restaurantId}
                        active={restaurantId === restaurant.restaurantId}
                        restaurant={restaurant as unknown as DeliveringRestaurantDto}
                      />
                    ))}
                  </>
                )}
              </div>
              {isPickupOrEatin &&
                restaurantLimit < visibleRestaurants?.length &&
                (!isPreorder || preorderTime) && (
                  <ShowMoreButton
                    width="medium"
                    onClick={() => setRestaurantLimitExtra((extra) => extra + 5)}
                  >
                    Näytä lisää ravintoloita
                  </ShowMoreButton>
                )}
              {orderType === OrderType.DELIVERY &&
                !isPreorder &&
                listHasDynamicPrice &&
                !freeDeliveryCampaign && (
                  <StyledAccordion $hideLine={true}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon htmlColor={colors.textSecondary} />}
                      aria-controls="kuljetustiedot-content"
                      id="-header"
                    >
                      <AccordionHeader>Näin kuljetushinta lasketaan</AccordionHeader>
                    </AccordionSummary>

                    <StyledAccordionDetails id="kuljetustiedot-content">
                      <DynamicDeliveryFeeInfo />
                    </StyledAccordionDetails>
                  </StyledAccordion>
                )}
            </RestaurantListSection>
          </SteppedContainer>
        </>
      )}
      {isDeliveryOrder && (
        <FeedbackLink href="/kuljetuspalaute">
          {visibleRestaurants.length > 0
            ? 'Etkö löydä sopivaa ravintolaa?'
            : 'Lähetä toive kuljetuksesta'}
        </FeedbackLink>
      )}
    </StyledLoadingContainer>
  ) : (
    <StyledLoadingContainer loading={false} />
  );
};

type ErrorDetails = {
  message: string;
  severity: InfoBoxTypes;
  contentBlocked: boolean;
};

const useSearchResultStatus = (
  restaurants: (RestaurantDetailsDto | DeliveringRestaurantDto)[],
  deliveryType: OrderType,
  restaurantError: Error | RestaurantsNotFound | null
): ErrorDetails | null => {
  const address = useAddress();

  return useMemo(() => {
    if (
      restaurantError &&
      !(restaurantError instanceof RestaurantsNotFound) &&
      !(restaurantError instanceof PreselectedRestaurantOutOfReach)
    ) {
      return {
        severity: InfoBoxTypes.DANGER,
        message: 'Kääk, ravintoloiden haku ei onnistunut. Kokeile hetken päästä uudelleen.',
        contentBlocked: true,
      };
    }

    if (deliveryType === OrderType.DELIVERY) {
      if (restaurantError instanceof RestaurantsNotFound) {
        return {
          severity: InfoBoxTypes.WARNING,
          message:
            'Osoitteelle ei löydy yhtään kuljetusta tarjoavaa ravintolaa. Kokeile valita toimitustavaksi Nouto.',
          contentBlocked: true,
        };
      }

      if (restaurantError instanceof PreselectedRestaurantOutOfReach) {
        return {
          severity: InfoBoxTypes.WARNING,
          message: `Osoitteesi on liian kaukana hakemastasi ravintolasta. ${
            address
              ? `Seuraavat ravintolat kuljettavat osoitteeseen ${address.streetAddress}, ${address.city}.`
              : ''
          }`,
          contentBlocked: false,
        };
      }

      if (restaurants.length > 0 && !listHasOpenForDeliveryRestaurant(restaurants)) {
        // Singular vs plural
        return {
          severity: InfoBoxTypes.WARNING,
          message: `${
            restaurants?.length === 1 ? 'Ravintola ei' : 'Ravintolat eivät'
          } kuljeta tällä hetkellä, ole hyvä ja valitse toinen toimitustapa tai klikkaa 'Teen ennakkotilauksen'.`,
          contentBlocked: false,
        };
      }
    } else if (restaurantError instanceof RestaurantsNotFound) {
      return {
        severity: InfoBoxTypes.WARNING,
        message: 'Haulla ei löytynyt ravintoloita',
        contentBlocked: true,
      };
    }

    return null;
  }, [restaurants, deliveryType, restaurantError, address]);
};

export const RestaurantListSection = styled.div`
  width: 100%;
  min-height: 100px;
  padding-bottom: 48px;
`;

const StyledPreorderTimeSelection = styled(PreorderTimeSelection)`
  margin-bottom: 16px;
`;

const StyledLoadingContainer = styled(LoadingContainer)`
  min-height: 200px;

  ${LoadingIconContainer} {
    top: 30px;
    bottom: initial;
  }
`;

const RestaurantsWarningBox = styled(Snackbar)`
  margin: 16px auto;
  max-width: 720px;
  border-radius: 4px;
`;

const FeedbackLink = styled(typography.Link)`
  color: ${colors.textSecondary};
  display: flex;
  margin: 40px auto;
  max-width: 720px;

  &:hover,
  &:focus {
    color: ${colors.textSecondary};
  }

  @media ${mediaQuery.medium} {
    padding: 32px;
    max-width: none;
  }
`;

const AccordionHeader = styled.div`
  color: ${colors.textSecondary};
  text-decoration: underline;
  text-align: right;

  &:hover,
  &:focus {
    color: ${colors.textSecondary};
  }
`;

const CenteredContainer = styled.div`
  display: flex;
  flex-direction: column;
  color: ${colors.textDefault};
  background-color: ${colors.white};
  padding: 32px calc((100% - 720px) / 2) 32px calc((100% - 720px) / 2);

  @media ${mediaQuery.medium} {
    padding-left: 24px;
    padding-right: 24px;
  }
`;

const ShowMoreButton = styled(PrimaryButton)`
  margin-top: 16px;
`;
